import { CheckCircleIcon, CheckIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Input } from '../../components/UI';
import { useFeatureFlags } from '../../hooks';
import { analytics } from '../../index';
import { createAccountPublic } from '../../services/login/signup_public';
import { BookingShell, InlineError } from '../book/components/BookingShell.component';
import { useEmbed } from '../book/useEmbed';
import { HoneypotField } from './HoneypotField.component';
import { TermsOfServiceText, TOS_VERSION } from './TermsOfService.component';
import GetStarted from '../get_started/GetStarted.component';
import { RULES } from './password_rules';

const SUBMISSION = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// #region NewAccountRoute
/** /new-account: the Get started form as a full sign-up page, or — inside another site's iframe with ?embed=1 — just the password step. */
export const NewAccountRoute: React.FunctionComponent = (): React.ReactElement => {
    const [params] = useSearchParams();
    return params.get('embed') === '1' && window.parent !== window ? <SignupEmbed /> : <GetStarted page="signup" />;
};

type Phase = 'form' | 'saving' | 'done' | 'closed';

// #region SignupEmbed
/**
 * The last step of the website's sign-up. The site already asked who they are
 * and what they do (and saved it as request `r`), passing name, email and brand
 * in the URL fragment so they never reach a server log. Here they only choose a
 * password and accept the terms; the account, its CRM lead and those answers
 * are joined up by the backend.
 */
const SignupEmbed: React.FunctionComponent = (): React.ReactElement => {
    const [params] = useSearchParams();
    useEmbed(params);
    const [FeatureFlags] = useFeatureFlags();
    const prefill = React.useMemo(() => new URLSearchParams(window.location.hash.replace(/^#/, '')), []);
    const submissionId = SUBMISSION.test(params.get('r') ?? '') ? params.get('r') : null;
    const [who, setWho] = React.useState({ first: prefill.get('first') ?? '', last: prefill.get('last') ?? '', email: prefill.get('email') ?? '', company: prefill.get('company') ?? '' });
    const known = React.useRef(Boolean(who.first && who.last && /\S+@\S+\.\S+/.test(who.email) && who.company)).current;
    const [password, setPassword] = React.useState('');
    const [reveal, setReveal] = React.useState(false);
    const [terms, setTerms] = React.useState(false);
    const [showTerms, setShowTerms] = React.useState(false);
    const [phase, setPhase] = React.useState<Phase>('form');
    const [error, setError] = React.useState<{ message: string; reason?: string } | null>(null);
    const [tried, setTried] = React.useState(false);
    const [honeypot, setHoneypot] = React.useState('');

    React.useEffect(() => { FeatureFlags.get('show-create-account').then((on: boolean) => { if (!on) setPhase('closed'); }); }, []);

    const strong = RULES.every(([, ok]) => ok(password));
    const complete = Boolean(who.first.trim() && who.last.trim() && /\S+@\S+\.\S+/.test(who.email) && who.company.trim());

    const submit = async (skipDuplicateCheck = false) => {
        setTried(true);
        if (!complete || !strong || !terms) return;
        setPhase('saving'); setError(null);
        const result = await createAccountPublic({ ...who, password, submissionId, skipDuplicateCheck, tosVersion: TOS_VERSION, honeypot });
        if (!result.ok) { setPhase('form'); setError({ message: result.message, reason: result.reason }); return; }
        if (!honeypot) analytics.track('SIGNUP');
        setPhase('done');
        // Only the request id leaves the frame — never the name or email.
        window.parent.postMessage({ type: 'wallmates:signed_up', submission_id: submissionId }, '*');
    };

    if (phase === 'closed') return <BookingShell><div className="p-6 sm:p-8"><h2 className="text-lg font-semibold">Sign-ups are paused</h2><p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Book a call and we&apos;ll set your account up with you.</p></div></BookingShell>;

    if (phase === 'done') return (
        <BookingShell>
            <div className="flex flex-col items-center gap-y-3 p-6 text-center sm:p-10">
                <CheckCircleIcon className="h-11 w-11 text-emerald-600" strokeWidth={1.5} />
                <h2 className="text-xl font-semibold">Check your inbox</h2>
                <p className="max-w-sm text-sm text-zinc-600 dark:text-zinc-400">We sent a verification link to <span className="font-medium text-zinc-900 dark:text-zinc-100">{who.email}</span>. Open it to finish setting up {who.company}.</p>
                <a href="/login" target="_blank" rel="noopener" className="mt-2 text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">Open Wallmates</a>
            </div>
        </BookingShell>
    );

    const field = (key: keyof typeof who, label: string, autoComplete: string, type = 'text') => (
        <label className="flex flex-col gap-y-1 text-sm font-medium"><span>{label}</span>
            <Input type={type} value={who[key]} autoComplete={autoComplete} maxLength={key === 'company' ? 160 : 254} invalid={tried && !who[key].trim()} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setWho({ ...who, [key]: ev.target.value })} />
        </label>
    );

    return (
        <BookingShell>
            <form className="flex flex-col gap-y-5 p-5 sm:p-7" onSubmit={(ev) => { ev.preventDefault(); submit(); }} noValidate>
                <div>
                    <h2 className="text-xl font-semibold tracking-tight">{known ? 'Choose a password' : 'Create your account'}</h2>
                    <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">Last step. You&apos;ll confirm your email next, then you&apos;re in.</p>
                </div>

                {known ? (
                    <div className="flex items-center gap-x-3 rounded-xl bg-surface-muted px-3.5 py-3">
                        <span aria-hidden className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">{(who.first[0] ?? '').toUpperCase()}{(who.last[0] ?? '').toUpperCase()}</span>
                        <div className="min-w-0 text-sm">
                            <div className="truncate font-medium">{who.first} {who.last} · {who.company}</div>
                            <div className="truncate text-zinc-500 dark:text-zinc-400">{who.email}</div>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {field('first', 'First name', 'given-name')}{field('last', 'Last name', 'family-name')}
                        <div className="sm:col-span-2">{field('email', 'Email', 'email', 'email')}</div>
                        <div className="sm:col-span-2">{field('company', 'Brand or studio', 'organization')}</div>
                    </div>
                )}

                <HoneypotField value={honeypot} onChange={setHoneypot} />

                <div className="flex flex-col gap-y-2">
                    {/* Lets password managers file the new password under the right account. */}
                    <input type="email" name="username" autoComplete="username" value={who.email} readOnly hidden />
                    <label htmlFor="signup-password" className="text-sm font-medium">Password</label>
                    <div className="relative">
                        <Input id="signup-password" type={reveal ? 'text' : 'password'} value={password} autoComplete="new-password" maxLength={64} autoFocus={known}
                            invalid={tried && !strong} aria-describedby="signup-rules" onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setPassword(ev.target.value)} />
                        <button type="button" onClick={() => setReveal(!reveal)} aria-label={reveal ? 'Hide password' : 'Show password'} className="absolute inset-y-0 right-2 my-auto h-7 w-7 rounded-md p-1 text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200">
                            {reveal ? <EyeSlashIcon /> : <EyeIcon />}
                        </button>
                    </div>
                    <ul id="signup-rules" className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
                        {RULES.map(([label, ok]) => {
                            const met = ok(password);
                            return <li key={label} className={`flex items-center gap-1 ${met ? 'text-emerald-700 dark:text-emerald-400' : tried ? 'text-red-600 dark:text-red-400' : 'text-zinc-500'}`}><CheckIcon className={`h-3.5 w-3.5 ${met ? '' : 'opacity-30'}`} strokeWidth={2.5} />{label}</li>;
                        })}
                    </ul>
                </div>

                <div className="flex flex-col gap-y-2">
                    <label className="flex items-start gap-2 text-sm text-zinc-700 dark:text-zinc-300">
                        <input type="checkbox" className="mt-0.5 h-4 w-4 rounded border-surface-border" checked={terms} onChange={(ev) => setTerms(ev.target.checked)} />
                        <span>I agree to the <button type="button" className="font-medium text-emerald-700 hover:underline dark:text-emerald-400" aria-expanded={showTerms} onClick={() => setShowTerms(!showTerms)}>Terms of Service</button></span>
                    </label>
                    {tried && !terms ? <span role="alert" className="text-xs text-red-600 dark:text-red-400">Please accept the terms to continue.</span> : null}
                    {showTerms ? <div className="max-h-64 overflow-y-auto rounded-xl border border-surface-border text-sm"><TermsOfServiceText compact /></div> : null}
                </div>

                {error ? (
                    <div className="flex flex-col gap-y-2">
                        <InlineError message={error.reason === 'duplicate_brand' ? `${error.message} If you work with them, ask their admin to invite you instead.` : error.message} />
                        {error.reason === 'exists' ? <a href="/login" target="_blank" rel="noopener" className="text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">Sign in instead</a> : null}
                        {error.reason === 'duplicate_brand' ? <button type="button" onClick={() => submit(true)} className="self-start text-sm font-medium text-emerald-700 hover:underline dark:text-emerald-400">It&apos;s a different brand. Create my account</button> : null}
                    </div>
                ) : null}

                <button type="submit" disabled={phase === 'saving'} className="inline-flex h-11 items-center justify-center rounded-xl bg-emerald-600 px-5 text-sm font-semibold text-white transition hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2 disabled:opacity-60">
                    {phase === 'saving' ? 'Creating your account…' : 'Create account'}
                </button>
            </form>
        </BookingShell>
    );
};
